import { Filter } from '../types/filter';
import { FilterItemConfig } from '../types/filter-config';
import { FilterOperator } from '../types/filter-operators';

/** in/equals read naturally as "Title: value"; every other operator shows its glyph */
export function showsOperatorGlyph(operator: FilterOperator): boolean {
  return operator !== 'in' && operator !== 'equals';
}

export type FilterValueSummary =
  | { kind: 'text'; text: string }
  | { kind: 'count'; count: number }
  | { kind: 'range-reference'; range: string; reference: string }
  | null;

/**
 * The text every surface shows for a summary: the formatted value, or the count - with the
 * "ausgewählt" suffix where there is room (sidebar row, pinned dropdown), bare in a chip.
 * An empty formatted value is spelled out as "(Leer)", never left as a dangling colon.
 */
export function formatFilterValueSummary(
  summary: FilterValueSummary,
  t: (key: string) => () => string,
  options: { countSuffix: boolean } = { countSuffix: true }
): string | null {
  if (!summary) return null;
  if (summary.kind === 'count')
    return options.countSuffix
      ? `${summary.count} ${t('#CORE_FILTER.CUSTOM.SELECTED')()}`
      : `${summary.count}`;
  if (summary.kind === 'range-reference')
    return `${summary.range} ${t('#CORE_FILTER.DATE.VERSUS')()} ${summary.reference}`;
  return summary.text === '' ? t('#CORE_FILTER.MULTI_SELECT.EMPTY_VALUE')() : summary.text;
}

/**
 * Summarizes a field filter's value for compact labels (menu bar, chips):
 * single values format via the item's formatter, multi-selections report a count.
 */
export function summarizeFilterValue<T extends object>(
  config: FilterItemConfig<T>,
  filter: Filter<T> | null
): FilterValueSummary {
  if (!filter || filter.type === 'conditions') return null;
  const value = (filter as { value?: unknown }).value;
  if (value === undefined || value === null) return null;
  const format = (v: unknown) => config.formatter(v as never);
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (value.length === 1) return { kind: 'text', text: format(value[0]) };
    return { kind: 'count', count: value.length };
  }
  if (typeof value === 'object' && 'from' in value && 'to' in value) {
    const range = value as { from: unknown; to: unknown };
    const text = `${format(range.from)} – ${format(range.to)}`;
    const reference = (filter as { reference?: { from: unknown; to: unknown } }).reference;
    if (reference)
      return { kind: 'range-reference', range: text, reference: `${format(reference.from)} – ${format(reference.to)}` };
    return { kind: 'text', text };
  }
  return { kind: 'text', text: format(value) };
}
